import React, { useEffect, useState, useRef } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CardIcon } from "../../assects/svg/dashboard/Welcome_Card_icon.svg";

import { useAuth } from "../../context/auth/AuthContext";
import { BASE_URL } from "../api/url";
import { getDashboardStats, getTotalActiveClients } from "../../services/dashboard";
import "../styles/useCustomScrollbar";
import PageHeader from "../Ui_Components/PageHeader";
import Pagination from "../Ui_Components/Pagination";
import useCustomScrollbar from "../styles/useCustomScrollbar";

interface MealLogCount {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

interface Client {
  user_id: number;
  user_name: string;
  user_profile: string | null;
  goal: string;
  meal_log_count: MealLogCount;
  meal_log_status: string;
  subscription_period: string;
  subscription_type: string;
  activity_days: number;
  primary_nutrist_name: string;
  backup_nutrist_names: string | null;
  nutritionist_names: string[];
  purchase_date: string;
  duration_days: number;
  subscription_end_date: string;
  days_left: number;
  plan_status: string;
}

interface DashboardStats {
  user_count: number;
  active_count: number;
  new_assigned_count: number;
}

interface ClientListResponse {
  success: string;
  message: string;
  total_count: number;
  total_pages: number;
  current_page: number;
  data: Client[];
}

// Global cache - persists across component unmounts
const pageCache = new Map<number, Client[]>();
let cachedTotalPages = 1;
let cachedTotalCount = 0;

const Dashboard: React.FC = () => {
  useCustomScrollbar();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Helper function to trim subscription type
  const trimSubscriptionType = (subscriptionType: string): string => {
    return subscriptionType.replace(/\s*\([^)]*\)/g, "").trim();
  };

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(cachedTotalPages);
  const [totalCount, setTotalCount] = useState(cachedTotalCount);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Client[] | null>(null); // null = no active search
  const [searchLoading, setSearchLoading] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    user_count: 0,
    active_count: 0,
    new_assigned_count: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Track prefetch operations to avoid duplicates
  const prefetchingPages = useRef(new Set<number>());
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get user's first name from AuthContext
  const getUserName = (): string => {
    if (!user) return "User";
    return user.nutrist_fname || "User";
  };

  // Navigate to Client page with New Assigned tab
  const handleNewAssignedClick = () => {
    navigate("/client", { state: { activeTab: "newAssigned" } });
  };

  // Navigate to client detail page
  const handleClientClick = (userId: number) => {
    navigate(`/client/${userId}`);
  };

  // Fetch client list for a specific page
  const fetchClientList = async (
    page: number,
    isPrefetch = false,
  ): Promise<void> => {
    if (!user?.nutrist_id) {
      if (!isPrefetch) setLoading(false);
      return;
    }

    // Skip cache when search is active
    if (pageCache.has(page)) {
      if (!isPrefetch) {
        setClients(pageCache.get(page)!);
        setTotalPages(cachedTotalPages);
        setTotalCount(cachedTotalCount);
        setLoading(false);
      }
      return;
    }

    if (isPrefetch && prefetchingPages.current.has(page)) return;

    try {
      if (isPrefetch) {
        prefetchingPages.current.add(page);
      } else {
        setLoading(true);
      }

      const data: ClientListResponse = await getTotalActiveClients(
        user.nutrist_id,
        "active",
        page,
      );

      if (data.success === "1") {
        pageCache.set(page, data.data);
        cachedTotalPages = data.total_pages;
        cachedTotalCount = data.total_count;

        if (!isPrefetch) {
          setClients(data.data);
          setTotalPages(data.total_pages);
          setTotalCount(data.total_count);
          setLoading(false);
        }

        if (!isPrefetch && page < data.total_pages) {
          setTimeout(() => fetchClientList(page + 1, true), 100);
        }
      } else {
        console.error("Failed to fetch client list:", data.message);
        if (!isPrefetch) setLoading(false);
      }
    } catch (err) {
      console.error(`Error fetching page ${page}:`, err);
      if (!isPrefetch) setLoading(false);
    } finally {
      if (isPrefetch) prefetchingPages.current.delete(page);
    }
  };

  // Load current page data when page changes
  useEffect(() => {
    fetchClientList(currentPage, false);
  }, [user?.nutrist_id, currentPage]);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (!user?.nutrist_id) {
        setStatsError("No nutritionist ID found");
        setStatsLoading(false);
        return;
      }

      try {
        setStatsLoading(true);
        const data = await getDashboardStats(user.nutrist_id);

        if (data.success === "1") {
          setStats({
            user_count: data.user_count || 0,
            active_count: data.active_count || 0,
            new_assigned_count: data.new_assigned_count || 0,
          });
          setStatsError(null);
        } else {
          setStatsError(data.message || "Failed to fetch dashboard stats");
        }
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setStatsError("Failed to load dashboard statistics");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchDashboardStats();
  }, [user?.nutrist_id]);

  // Debounced API search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!searchQuery.trim()) {
      setSearchResults(null);
      setCurrentPage(1);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      if (!user?.nutrist_id) return;
      setSearchLoading(true);
      try {
        const data: ClientListResponse = await getTotalActiveClients(
          user.nutrist_id,
          "active",
          1, // page ignored for search
          searchQuery.trim(),
        );
        if (data.success === "1") {
          setSearchResults(data.data);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, user?.nutrist_id]);

  // Returns search results when active, otherwise normal page data
  const getCurrentData = (): Client[] => {
    if (searchResults !== null) return searchResults;
    return clients;
  };

  const displayedClients = getCurrentData();
  const isSearchActive = searchQuery.trim().length > 0;

  // Get meal log dots based on meal_log_count
  const getMealLogDots = (mealLogCount: MealLogCount) => {
    const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
    const dots = [];

    const currentDayIndex = new Date().getDay();
    const adjustedCurrentDay = currentDayIndex === 0 ? 6 : currentDayIndex - 1;

    for (let i = 0; i < 7; i++) {
      const dayCount = mealLogCount[days[i]];
      const isCurrentDay = i === adjustedCurrentDay;
      const isFutureDay = i > adjustedCurrentDay;

      if (isCurrentDay) {
        dots.push(
          <div
            key={i}
            className="w-3 h-3 rounded-full flex items-center justify-center"
            style={{ border: "4px solid #0DB5B5" }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#ffffff" }}
            ></div>
          </div>,
        );
      } else if (isFutureDay) {
        dots.push(
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: "#D9D9D9" }}
          ></div>,
        );
      } else {
        dots.push(
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: dayCount === 1 ? "#00B32D" : "#FFA628",
            }}
          ></div>,
        );
      }
    }
    return dots;
  };

  // Get user avatar or initials
  const getUserAvatar = (client: Client) => {
    if (client.user_profile) {
      return (
        <img
          src={`${BASE_URL}${client.user_profile}`}
          alt={client.user_name}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
        />
      );
    }

    const initials = client.user_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return (
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-white text-sm sm:text-base font-semibold flex-shrink-0">
        {initials}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      {/* Header */}
      <PageHeader title="Dashboard" />

      {/* Dashboard Content */}
      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        {/* Welcome Card and Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4 flex-shrink-0">
          {/* Welcome Card */}
          <div className="lg:col-span-5 bg-[#F1FBFB] border-[0.5px] border-[#6DD0CF] rounded-[6px] p-6 sm:px-8 text-[#003E3E] relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-xl sm:text-2xl 3xl:text-3xl font-semibold font-inter mb-2">
                Hi, {getUserName()}
              </h2>
              <p className="text-sm sm:text-base 3xl:text-[18px] font-inter text-[#003E3E]">
                Welcome to your Triva Team
              </p>
            </div>
            <div className="absolute -right-9 top-1/2 -translate-y-1/2 z-0">
              <CardIcon className="w-24 h-24 sm:w-32 sm:h-32" />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-4">
            <div className="bg-white rounded-[6px] p-4 sm:px-4 sm:py-4 sm:pt-6 border-[0.5px] border-[#F3F4F6]">
              <div className="text-xs sm:text-[18px] font-inter text-[#646464] mb-4">
                Total clients
              </div>
              <div className="text-3xl sm:text-[26px] font-semibold text-[#1F1F1F]">
                {stats.user_count}
              </div>
            </div>

            <div className="bg-white rounded-[6px] p-4 sm:px-4 sm:py-4 sm:pt-6 border-[0.5px] border-[#F3F4F6]">
              <div className="text-xs sm:text-[18px] font-inter text-[#646464] mb-4">
                Active clients
              </div>
              <div className="text-3xl sm:text-[26px] font-semibold text-[#1F1F1F]">
                {stats.active_count}
              </div>
            </div>

            <div
              onClick={handleNewAssignedClick}
              className="bg-white rounded-[6px] p-4 sm:px-4 sm:py-4 sm:pt-6 border-[0.5px] border-[#F3F4F6] cursor-pointer"
            >
              <div>
                <div className="text-xs sm:text-[18px] font-inter text-[#646464] mb-4">
                  New assigned
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-3xl sm:text-[26px] font-semibold text-[#1F1F1F]">
                    {stats.new_assigned_count}
                  </div>
                  <button
                    className="text-[#00A9A9] hover:text-[#008888] transition-colors cursor-pointer"
                    aria-label="View new assigned clients"
                  >
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 30 30"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 17L17 7M17 7H7M17 7V17"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client List */}
        <div className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">
          {/* Fixed Header with Search */}
          <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
              Total active clients (
              {statsLoading
                ? "..."
                : isSearchActive
                  ? displayedClients.length
                  : stats.active_count}
              )
            </h3>
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm"
              />
              <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Scrollable Table Container */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {loading || searchLoading ? (
              <div className="flex items-center justify-center flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-base text-gray-500">Loading data</p>
                </div>
              </div>
            ) : (
              <>
                {/* Fixed Table Header */}
                <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                  <table className="w-full min-w-[640px] table-fixed">
                    <thead>
                      <tr className="bg-white">
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[30%]">
                          Client name
                        </th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[19%]">
                          Goal
                        </th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[18%]">
                          Activity
                        </th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[18%]">
                          Meal log
                        </th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[15%]">
                          Status
                        </th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Scrollable Table Body */}
                <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                  {displayedClients.length === 0 ? (
                    <div className="flex items-center justify-center h-full py-16">
                      <p className="text-gray-400 text-sm sm:text-base font-inter">
                        {isSearchActive
                          ? "No active clients match your search"
                          : "No active clients available"}
                      </p>
                    </div>
                  ) : (
                    <table className="w-full min-w-[640px] table-fixed">
                      <tbody>
                        {displayedClients.map((client, index) => (
                          <tr
                            key={client.user_id}
                            onClick={() => handleClientClick(client.user_id)}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <td
                              className={`py-3 sm:py-4 px-4 sm:px-6 w-[30%] ${
                                index !== displayedClients.length - 1
                                  ? "border-b border-[#111C2D1A]"
                                  : ""
                              }`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                {getUserAvatar(client)}
                                <div>
                                  <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">
                                    {client.user_name}
                                  </div>
                                  <div className="text-xs sm:text-sm font-inter text-gray-500">
                                    {trimSubscriptionType(
                                      client.subscription_type,
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[19%] ${
                                index !== displayedClients.length - 1
                                  ? "border-b border-[#111C2D1A]"
                                  : ""
                              }`}
                            >
                              {client.goal}
                            </td>
                            <td
                              className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[18%] ${
                                index !== displayedClients.length - 1
                                  ? "border-b border-[#111C2D1A]"
                                  : ""
                              }`}
                            >
                              {client.activity_days}/{client.duration_days}
                            </td>
                            <td
                              className={`py-3 sm:py-4 px-4 sm:px-6 w-[18%] ${
                                index !== displayedClients.length - 1
                                  ? "border-b border-[#111C2D1A]"
                                  : ""
                              }`}
                            >
                              <div className="flex gap-1">
                                {getMealLogDots(client.meal_log_count)}
                              </div>
                            </td>
                            <td
                              className={`py-3 sm:py-4 px-4 sm:px-6 w-[15%] ${
                                index !== displayedClients.length - 1
                                  ? "border-b border-[#111C2D1A]"
                                  : ""
                              }`}
                            >
                              <span
                                className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-[12px] font-semibold font-inter ${
                                  client.plan_status === "active"
                                    ? "bg-[#EBF7F4] text-[#0DB5B5]"
                                    : client.plan_status === "ending_soon"
                                      ? "bg-[#FFF3DE] text-[#FF9500]"
                                      : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {client.plan_status
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1),
                                  )
                                  .join(" ")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Pagination — hidden while search is active */}
          {!isSearchActive && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              resultCount={clients.length}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;